"use client";
import {JSX} from "react";

import Image from "next/image";

import {type LocationOption, LocationSearchDrawerView} from "@/features/locations";
import {useMobileSearch} from "@/features/rental";
import {PetsToggle} from "@/features/rental/guests/parts";

import {Collapsible} from "@/shared/ui/data-display/collapsible";
import {ScrollableCalendar} from "@/shared/ui/form/datepickers/scrollable-calendar";
import {StepperField} from "@/shared/ui/form/steppers/stepper-field";
import {getStaticPathIcon} from "@/shared/utils/get-static-path-icon";

import {SearchDrawer, SearchDrawerFooter} from "./parts";

import "./styles/mobile-search.view.scss";

interface MobileSearchViewProps {
    className?: string;
    initialLocation?: LocationOption;
}

export function MobileSearchView({className, initialLocation}: MobileSearchViewProps): JSX.Element {
    const {
        search,
        isOpen,
        activeSection,
        isSearchFocused,
        locationError,
        isSubmitting,
        containerRef,
        submitLabel,
        submitVariant,
        handleOpen,
        handleClose,
        toggleSection,
        handleSearchFocusChange,
        handleLocationSelect,
        handleClear,
        handleNextStep,
        getDateRangeValue,
        getGuestsValue,
        t,
    } = useMobileSearch({initialLocation});
    return (
        <div ref={containerRef} className={`mobile-search ${className || ""}`}>
            <button type="button" className="mobile-search__trigger" onClick={handleOpen}>
                <Image src={getStaticPathIcon("search")} alt="" width={18} height={18} className="mobile-search__trigger-icon" />
                <span className="mobile-search__trigger-text">{t("locationPlaceholder")}</span>
            </button>

            <SearchDrawer
                isOpen={isOpen}
                onClose={handleClose}
                footer={
                    <SearchDrawerFooter
                        onClear={handleClear}
                        onSubmit={handleNextStep}
                        clearLabel={t("clearAll")}
                        submitLabel={submitLabel}
                        submitVariant={submitVariant}
                        isLoading={isSubmitting}
                    />
                }
            >
                <Collapsible
                    label={t("where")}
                    value={search.locationSearch.selectedOption?.name || t("searchDestinations")}
                    isOpen={activeSection === "where"}
                    onToggle={() => toggleSection("where")}
                    fullscreen={isSearchFocused}
                    error={locationError}
                >
                    <LocationSearchDrawerView
                        model={search.locationSearch}
                        placeholder={t("locationPlaceholder")}
                        isFullscreen={isSearchFocused}
                        onFocusChange={handleSearchFocusChange}
                        onSelect={handleLocationSelect}
                    />
                </Collapsible>

                <Collapsible
                    label={t("when")}
                    value={getDateRangeValue() || t("addDates")}
                    isOpen={activeSection === "when"}
                    onToggle={() => toggleSection("when")}
                >
                    <ScrollableCalendar model={search.dateRange} />
                </Collapsible>

                {activeSection !== "when" && (
                    <Collapsible
                        label={t("who")}
                        value={getGuestsValue() || t("addGuests")}
                        isOpen={activeSection === "who"}
                        onToggle={() => toggleSection("who")}
                    >
                        <div className="collapsible__steppers">
                            <StepperField model={search.guests.adults} label={t("adults")} />
                            <StepperField model={search.guests.children} label={t("children")} />
                            <PetsToggle model={search.guests} />
                        </div>
                    </Collapsible>
                )}
            </SearchDrawer>
        </div>
    );
}
